import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {useRecoilValue, useResetRecoilState, useSetRecoilState} from 'recoil';
import {
    AllCheckState,
    TermsAgreeState,
    RecoilStateList,
} from 'src/recoil/common';
import { monitoredOpen } from 'src/service/utils/sentry/navigation';
import * as S from 'src/styles/styledcomponent/views/mkt04/Terms';
import {JoinTermsDetail} from 'src/types/type';

const Terms = (): JSX.Element => {
    const params = useParams();
    const {
        TelType,
        AllTermsAgree,
        termsAgree,
        TermsData,
        isAgreeAllRequiredTerms,
    } = useRecoilValue(RecoilStateList);

    const setAllTermsAgree = useSetRecoilState(AllCheckState);
    const setTermsAgree = useSetRecoilState(TermsAgreeState);
    const ResetJoinTerms = useResetRecoilState(TermsAgreeState);

    // 필수/선택 분리
    const {requiredTerms, optionalTerms} = useMemo(() => {
        const list = TermsData?.Terms ?? [];
        return {
            requiredTerms: list.filter(t => t.Required === 1 && t.Order != null),
            optionalTerms: list.filter(t => t.Required !== 1 && t.Order != null),
        };
    }, [TermsData]);


    const [openRequired, setOpenRequired] = useState<boolean>(false);
    const [openOptional, setOpenOptional] = useState<boolean>(false);

    const termsOpen = (order: number): void => {
        const width = 400;
        const height = 600;
        const url = params?.requestID
            ? `/terms/${params.requestID}/${TelType}/${TermsData?.TermsIdx}/${order}`
            : undefined;
        if (url) monitoredOpen(url, 'termsOpen', '_blank', {
          windowFeatures: `width=${width}, height=${height}`,
        })
    };

    const AllTerms = (): void => {
        const arrCopy = [...termsAgree];
        if (!AllTermsAgree) {
            TermsData?.Terms?.forEach((t: JoinTermsDetail) => {
                if (t.Order !== undefined && t.Required === 1) {
                    arrCopy[t.Order - 1] = 1;
                }
            });
        } else {
            TermsData?.Terms?.forEach((t: JoinTermsDetail) => {
                if (t.Order !== undefined && t.Required === 1) {
                    arrCopy[t.Order - 1] = 0;
                }
            });
        }
        setTermsAgree(arrCopy);
        setAllTermsAgree(prev => !prev);
    };

    const EachTerms = (order: number): void => {
        const arrCopy = [...termsAgree];
        arrCopy[order - 1] = termsAgree[order - 1] === 1 ? 0 : 1;
        setTermsAgree(arrCopy);
    };

    useEffect(() => {
        ResetJoinTerms();
        setAllTermsAgree(false);
    }, [ResetJoinTerms, TelType, TermsData, setAllTermsAgree]);

    useEffect(() => {
        setAllTermsAgree(isAgreeAllRequiredTerms);
    }, [isAgreeAllRequiredTerms, setAllTermsAgree]);


    const panelId = 'required-terms-panel';
    const optionalPanelId = 'optional-terms-panel';


    const allOptionalChecked = useMemo(() => {
        if (!optionalTerms.length) return false;
        return optionalTerms.every(t => termsAgree[(t.Order as number) - 1] === 1);
    }, [optionalTerms, termsAgree]);

    const toggleOptionalAll = (): void => {
        const arrCopy = [...termsAgree];
        if (allOptionalChecked) {
            optionalTerms.forEach(t => {
                const idx = (t.Order as number) - 1;
                arrCopy[idx] = 0;
            });
        } else {
            optionalTerms.forEach(t => {
                const idx = (t.Order as number) - 1;
                arrCopy[idx] = 1;
            });
        }
        setTermsAgree(arrCopy);
    };
    return (
        <S.TermsAgreeWrap $dim={AllTermsAgree}>
            {/* 전체 동의 */}
            <S.AllTermsContainer>
                <S.HiddenCheckbox
                    id="all-terms"
                    checked={AllTermsAgree}
                    onChange={AllTerms}
                />
                <S.CheckIcon checked={AllTermsAgree} variant="all"/>
                <p>필수 약관 모두 동의하기</p>
            </S.AllTermsContainer>

            {/* 필수 약관 아코디언 */}
            <S.Accordion>
                <S.EachTermsItem>
                    <S.CheckLabel htmlFor="required-all">
                        <S.HiddenCheckbox
                            id="required-all"
                            checked={AllTermsAgree}
                            onChange={AllTerms}
                        />
                        <S.CheckIcon checked={AllTermsAgree} variant="each"/>
                    </S.CheckLabel>
                    <S.TermsTitleWrap>
                        <S.TermsTitle>[필수] 서비스 필수 약관</S.TermsTitle>
                        <S.ChevronButton
                            type="button"
                            aria-controls={panelId}
                            aria-expanded={openRequired}
                            onClick={() => setOpenRequired(o => !o)}
                        >
                            <S.AccordionChevron $open={openRequired} aria-hidden/>
                        </S.ChevronButton>
                    </S.TermsTitleWrap>
                </S.EachTermsItem>
                {openRequired && (
                    <S.AccordionPanel id={panelId}>
                        {requiredTerms.map(t => {
                            const order = t.Order!;
                            const id = `term-${order}`;
                            const checked = termsAgree[order - 1] === 1;
                            return (
                                <S.EachTermsItem key={id}>
                                    <S.CheckLabel htmlFor={id}>
                                        <S.HiddenCheckbox
                                            id={id}
                                            checked={checked}
                                            onChange={() => EachTerms(order)}
                                        />
                                        <S.CheckIcon checked={checked} variant="each"/>
                                    </S.CheckLabel>
                                    <S.TermsTitleWrap>
                                        <S.TermsTitle>
                                            {t.Title}
                                        </S.TermsTitle>
                                        <S.ChevronButton
                                            type="button"
                                            onClick={() => termsOpen(order)}
                                        >
                                            <S.Chevron/>
                                        </S.ChevronButton>
                                    </S.TermsTitleWrap>
                                </S.EachTermsItem>
                            );
                        })}
                    </S.AccordionPanel>
                )}
            </S.Accordion>
            {optionalTerms.length > 0 && (
                <S.Accordion>
                    <S.EachTermsItem>
                        {/* ✅ 선택 전체 선택 토글 */}
                        <S.CheckLabel htmlFor="optional-all">
                            <S.HiddenCheckbox
                                id="optional-all"
                                checked={allOptionalChecked}
                                onChange={toggleOptionalAll}
                            />
                            <S.CheckIcon checked={allOptionalChecked} variant="each"/>
                        </S.CheckLabel>

                        <S.TermsTitleWrap>
                            <S.TermsTitle>[선택] 서비스 선택 약관</S.TermsTitle>
                            <S.ChevronButton
                                type="button"
                                aria-controls={optionalPanelId}
                                aria-expanded={openOptional}
                                onClick={() => setOpenOptional(o => !o)}
                            >
                                <S.AccordionChevron $open={openOptional} aria-hidden/>
                            </S.ChevronButton>
                        </S.TermsTitleWrap>
                    </S.EachTermsItem>

                    {openOptional && (
                        <S.AccordionPanel id={optionalPanelId}>
                            {optionalTerms.map(t => {
                                const order = t.Order!;
                                const id = `term-${order}`;
                                const checked = termsAgree[order - 1] === 1;
                                return (
                                    <S.EachTermsItem key={id}>
                                        <S.CheckLabel htmlFor={id}>
                                            <S.HiddenCheckbox
                                                id={id}
                                                checked={checked}
                                                onChange={() => EachTerms(order)}
                                            />
                                            <S.CheckIcon checked={checked} variant="each"/>
                                        </S.CheckLabel>

                                        <S.TermsTitleWrap>
                                            <S.TermsTitle>[선택] {t.Title}</S.TermsTitle>
                                            <S.ChevronButton type="button" onClick={() => termsOpen(order)}>
                                                <S.Chevron/>
                                            </S.ChevronButton>
                                        </S.TermsTitleWrap>
                                    </S.EachTermsItem>
                                );
                            })}
                        </S.AccordionPanel>
                    )}
                </S.Accordion>
            )}
        </S.TermsAgreeWrap>
    );
};

export default Terms;
